import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";

import { ScopedThemeProvider } from "@/react/components/Paywalls/Checkout/CheckoutPage/CheckoutDescription/Offer/ScopedThemeProvider";
import { OfferActionsContext } from "@/react/components/Paywalls/CheckoutWithSession/pages/OfferPage/OfferActionsContext";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { APPEARANCE } from "@/react/providers/Theme";

interface OfferActionsLayoutProps {
  actions: ReactNode;
  children: ReactNode;
  stickyActions?: ReactNode;
}

export const OfferActionsLayout = ({
  actions,
  children,
  stickyActions = actions,
}: OfferActionsLayoutProps) => {
  const scrollContainer = useRef<HTMLDivElement>(null);
  const [inlineActions, setInlineActions] = useState<HTMLDivElement | null>(
    null,
  );
  const [isSticky, setIsSticky] = useState(false);

  useEffect(() => {
    if (!inlineActions || !scrollContainer.current) return;
    const root = scrollContainer.current;
    const updateStickyState = () =>
      setIsSticky(
        inlineActions.getBoundingClientRect().bottom <=
          root.getBoundingClientRect().top,
      );

    const observer = new IntersectionObserver(updateStickyState, { root });
    observer.observe(inlineActions);
    // A scroll can jump from below to above the CTAs without changing their
    // intersection state, so also check their position on the scroll event.
    root.addEventListener("scroll", updateStickyState, { passive: true });
    return () => {
      observer.disconnect();
      root.removeEventListener("scroll", updateStickyState);
    };
  }, [inlineActions]);

  const shouldShowStickyActions = isSticky && Boolean(inlineActions && actions);
  const value = useMemo(
    () => ({
      actions,
      isSticky: shouldShowStickyActions,
      placement: "inline" as const,
      setInlineActions,
    }),
    [actions, shouldShowStickyActions],
  );
  const stickyValue = useMemo(
    () => ({ ...value, placement: "sticky" as const }),
    [value],
  );

  return (
    <OfferActionsContext.Provider value={value}>
      <div className="pointer-events-auto flex size-full min-h-0 flex-col overflow-hidden [container-type:inline-size]">
        <div
          ref={scrollContainer}
          className="min-h-0 flex-1 overflow-y-auto"
          data-testid="offer-scroll-container"
        >
          {children}
        </div>
        <div
          className={classNames(
            "grid max-h-full shrink-0 transition-[grid-template-rows] duration-200 motion-reduce:transition-none",
            {
              "grid-rows-[1fr]": shouldShowStickyActions,
              "grid-rows-[0fr]": !shouldShowStickyActions,
            },
          )}
          aria-hidden={!shouldShowStickyActions}
          inert={!shouldShowStickyActions}
          data-testid="offer-sticky-actions"
        >
          <ScopedThemeProvider appearance={APPEARANCE.LIGHT}>
            <div className="min-h-0 overflow-y-auto">
              <div
                className="flex justify-center border-t border-primary bg-primary px-5 py-3 md:px-10 [@container(min-width:768px)]:py-4"
                data-testid="offer-sticky-content"
              >
                <div className="w-full max-w-xl">
                  <OfferActionsContext.Provider value={stickyValue}>
                    {shouldShowStickyActions && stickyActions}
                  </OfferActionsContext.Provider>
                </div>
              </div>
            </div>
          </ScopedThemeProvider>
        </div>
      </div>
    </OfferActionsContext.Provider>
  );
};
